import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';

import { cx } from './internal/cx.js';

export interface LinkProps extends ComponentPropsWithoutRef<'a'> {
  children: ReactNode;
  /**
   * Underline the link at rest, not only on hover. Use where position alone does not mark the text
   * as a link — standalone links in page chrome (footers, banners) — or where the link colour is
   * overridden by a nested `Text`, so colour is not the sole signal (WCAG 1.4.1).
   */
  underline?: boolean;
}

/** Styled anchor for inline and breadcrumb navigation. */
export function Link({ children, className, underline = false, ...props }: LinkProps): JSX.Element {
  return (
    <a className={cx('nrx-link', underline && 'nrx-link--underline', className)} {...props}>
      {children}
    </a>
  );
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface BreadcrumbsProps extends ComponentPropsWithoutRef<'nav'> {
  items: BreadcrumbItem[];
  /**
   * Render linked crumbs with a router-aware component. The default plain anchor resolves `href`
   * against the document root, so an app mounted under a router basename must supply this.
   *
   * @example
   * ```tsx
   * <Breadcrumbs
   *   items={items}
   *   renderLink={({ href, label }) => (
   *     <Link className="nrx-link" to={href}>{label}</Link>
   *   )}
   * />
   * ```
   */
  renderLink?: (item: { href: string; label: string }) => ReactNode;
}

/** Hierarchical page trail for builder and list/detail flows. */
export function Breadcrumbs({
  items,
  className,
  renderLink,
  ...props
}: BreadcrumbsProps): JSX.Element {
  return (
    <nav aria-label="Breadcrumb" className={cx('nrx-breadcrumbs', className)} {...props}>
      <ol className="nrx-breadcrumbs__list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="nrx-breadcrumbs__item">
              {item.href && !isLast ? (
                (renderLink?.({ href: item.href, label: item.label }) ?? (
                  <Link href={item.href}>{item.label}</Link>
                ))
              ) : (
                <span aria-current={isLast ? 'page' : undefined}>{item.label}</span>
              )}
              {!isLast ? (
                <span className="nrx-breadcrumbs__separator" aria-hidden="true">
                  /
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
